/* iter v33 r24 · User M1500 — Backup registrazione sopralluogo.
 *
 * Problema originale: durante il sopralluogo l'agente registra la voce
 * con MediaRecorder ma se mette l'app in background su iOS Capacitor
 * PRIMA di premere "Concludi", la sessione si perde e i minuti registrati
 * volatilizzano. Feedback M1500 «avevo fatto una registrazione durante il
 * sopralluogo, poi ho tipo messo l'app in background e probabilmente non
 * avevo schiacciato concludi o che cosa e quindi me l'ha persa».
 *
 * Fix: salvataggio periodico dei chunk audio in IndexedDB (non
 * localStorage: audio blobs > 5MB rompono la quota). Se al riapri della
 * pagina RecordingPhase trova un backup < 24h nello store, propone il
 * recovery.
 *
 * API:
 *   saveRecordingChunk(sessionId, blob, durationS)  — throttled auto
 *   getStoredRecording()                            — al mount
 *   clearStoredRecording()                          — dopo upload OK
 */

const DB_NAME = "pronto_recording_backup_v1";
const STORE = "recordings";
const KEY = "current";
const MAX_AGE_MS = 24 * 3600 * 1000;
const _lastSave = { ts: 0 };
const SAVE_THROTTLE_MS = 5000; // salva ogni 5s max, evita IO thrash

function _openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Salva il blob corrente in IndexedDB.
 * @param {string} sessionId
 * @param {Blob} blob
 * @param {number} durationS
 * @param {{ force?: boolean }} [opts]
 */
export async function saveRecordingChunk(sessionId, blob, durationS, opts = {}) {
  const now = Date.now();
  if (!opts.force && now - _lastSave.ts < SAVE_THROTTLE_MS) return;
  _lastSave.ts = now;
  try {
    const db = await _openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(
      {
        sessionId,
        blob,
        durationS,
        mimeType: blob.type || "audio/webm",
        savedAt: now,
      },
      KEY,
    );
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    // Non fatal — se IndexedDB fallisce, la registrazione live continua
    console.warn("[recording-backup] save failed:", e);
  }
}

/**
 * @returns {Promise<null | { sessionId, blob, durationS, mimeType, savedAt }>}
 */
export async function getStoredRecording() {
  try {
    const db = await _openDb();
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(KEY);
    const rec = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!rec) return null;
    if (Date.now() - rec.savedAt > MAX_AGE_MS) {
      // Vecchio: puliamo e ignoriamo
      await clearStoredRecording();
      return null;
    }
    return rec;
  } catch (e) {
    console.warn("[recording-backup] get failed:", e);
    return null;
  }
}

export async function clearStoredRecording() {
  try {
    const db = await _openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(KEY);
    await new Promise((resolve) => { tx.oncomplete = resolve; });
    db.close();
    _lastSave.ts = 0;
  } catch (e) {
    console.warn("[recording-backup] clear failed:", e);
  }
}
